import { type JobsApi } from '@/features/jobs/api/job-api'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import {
  ApproveJobScopeChangeRequest,
  GetJobScopeChangeRequest,
  ListJobScopeChangesForCollaboratorRequest,
  RejectJobScopeChangeRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import type {
  AdminJobScopeUpdatedRealtimeEvent,
  JobScopeChangeApprovedRealtimeEvent,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import {
  JobScopeChange,
  JobScopeChangePriceBreakdown,
} from '@/features/jobs/job-scope-changes/JobScopeChange'
import {
  mapJobScopeChangePriceBreakdownToProps,
  mapJobScopeChangeToProps,
} from '@/features/jobs/job-scope-changes/job-scope-changes-mappers'
import { map, share } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import type { PrimeUserFn } from '@/features/iam'

/**
 * The store for {@link JobScopeChange}.
 */
export class JobScopeChangeStore {
  /**
   * The collection of {@link JobScopeChange}.
   */
  readonly jobScopeChanges = collection({
    create: () => new JobScopeChange(),
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
  })

  constructor(
    private readonly _api: JobsApi,
    private readonly _primeUser: PrimeUserFn,
  ) {
    this._api.realtime$
      .pipe(
        map(
          (evt) =>
            evt.getAdminJobScopeUpdated() ?? evt.getJobScopeChangeApproved(),
        ),
        filterNonNil(),
        share(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Returns the list of {@link JobScopeChange} for a given job id.
   */
  forJob(jobId: Id) {
    return this.jobScopeChanges.filter(
      (scopeChange) => scopeChange.jobId === jobId,
    )
  }

  /**
   * Returns the list of {@link JobScopeChange} sorted by created date.
   */
  forJobSortedByDate(jobId: Id) {
    return this.forJob(jobId).sort((a, b) => {
      return b.createdAt.getTime() - a.createdAt.getTime()
    })
  }

  /**
   * Fetches the list of {@link JobScopeChange} for a job.
   */
  async fetchScopeChangesForJob({
    jobId,
    signal,
  }: FetchScopeChangesForJobQuery) {
    const request = new ListJobScopeChangesForCollaboratorRequest().setJobId(
      Id.assert(jobId),
    )

    const response =
      await this._api.scopeChangeRequests.listJobScopeChangesForCollaborator(
        request,
      )

    signal?.throwIfAborted()

    const protos = response.getJobScopeChangesList()
    const props = protos.map((x) =>
      mapJobScopeChangeToProps(x, this._primeUser),
    )

    return this.jobScopeChanges.set(props)
  }

  /**
   * Fetches the {@link JobScopeChangePriceBreakdown} for a given job scope change id.
   */
  async fetchJobScopeChangePriceBreakdown({
    id,
    signal,
  }: FetchScopeChangePriceBreakdownQuery) {
    const request = new GetJobScopeChangeRequest().setId(Id.assert(id))

    const proto =
      await this._api.scopeChangeRequests.getJobScopeChangePriceBreakdownForClient(
        request,
      )

    signal?.throwIfAborted()

    return new JobScopeChangePriceBreakdown().set(
      mapJobScopeChangePriceBreakdownToProps(proto),
    )
  }

  /**
   * Approves the {@link JobScopeChange} for a given job.
   */
  async approveJobScopeChange(jobId: Id) {
    const request = new ApproveJobScopeChangeRequest().setJobId(
      Id.assert(jobId),
    )

    await this._api.scopeChangeRequests.approveJobScopeChange(request)

    await this.fetchScopeChangesForJob({
      jobId: jobId,
    })
  }

  /**
   * Declines the {@link JobScopeChange} for a given job.
   */
  async declineJobScopeChange({ jobId, reason }: DeclineJobScopeChange) {
    const request = new RejectJobScopeChangeRequest()
      .setJobId(Id.assert(jobId))
      .setReason(reason)

    await this._api.scopeChangeRequests.rejectJobScopeChange(request)

    await this.fetchScopeChangesForJob({
      jobId,
    })
  }

  /**
   * Handles the remote event.
   */
  private async _onRemoteEvent(
    proto:
      | AdminJobScopeUpdatedRealtimeEvent
      | JobScopeChangeApprovedRealtimeEvent,
  ) {
    if (!proto) {
      return null
    }

    const jobId = Id.assert(proto.getJobId())
    await this.fetchScopeChangesForJob({ jobId })
  }
}

export interface FetchScopeChangesForJobQuery {
  jobId: Id
  signal?: AbortSignal
}

export interface FetchScopeChangePriceBreakdownQuery {
  id: Id
  signal?: AbortSignal
}

export interface DeclineJobScopeChange {
  jobId: Id
  reason: string
}
